import { monthsInYear } from 'config'
import { PrepaidItemDescMap } from 'config/loan.bytepro.constants'
import { DateTime } from 'luxon'
import { applyPaymentFromEscrowOverrides } from 'pages/Loan/ClosingScreen/AggregateEscrow/escrowPayments'
import {
  getAmortizationSchedule,
  gethud1PageData,
  getSecondaryMarketingScreenData,
  getTrustLedger,
  getTrustLedgerInterest,
} from 'services'
import { formatDate, Num, removeComma, roundToCents, solveDecimalJavascriptSum } from 'utils'

const INITIAL_ESCROW_ACCOUNT_BALANCE_OV = 'Loan.InitialEscrowAccountBalanceOV'

const NEGATIVE_PA_KEYS = ['PAEscrowBalance', 'BPiece', 'ConstructionReserve', 'InterestReserve'] as const

export type FillProjectedSide = 'buyer' | 'seller'

export type ProjectedPurchaseAdviceValues = {
  PAUnpaidPrincipalBalance?: number
  PANetPrice?: number
  PAEscrowBalance?: number
  BPiece?: number
  ConstructionReserve?: number
  InterestReserve?: number
}

const parseInvestorDate = (value: string) => {
  const normalized = String(value || '').trim()
  if (!normalized) return null
  const iso = DateTime.fromISO(normalized)
  if (iso.isValid) return iso.startOf('day')
  const ymd = DateTime.fromFormat(normalized, 'yyyy-MM-dd')
  if (ymd.isValid) return ymd.startOf('day')
  const mdy = DateTime.fromFormat(normalized, 'MM/dd/yyyy')
  if (mdy.isValid) return mdy.startOf('day')
  return null
}

const buildAmortizationBalances = (data: Record<string, any>) => {
  const rows: Array<{ date: string; balance: number }> = []
  if (!data?.firstPaymentDate || !data?.loanTerm) return rows

  let balance = removeComma(data.loanAmount)
  const rate = removeComma(data.rate) / 100
  let firstPaymentDate = formatDate(data.firstPaymentDate)
  let mortgagePayment = removeComma(data.mortgagePayment)
  const additionalPrincipalReductionPeriod = removeComma(data.additionalPrincipalReductionPeriod) || 0
  const additionalPrincipalReductionAmount = removeComma(data.additionalPrincipalReductionAmount) || 0
  const addiPrincReductions: number[] = data.addiPrincReductions || []

  for (let i = 1; i <= data.loanTerm && balance > 0; i += 1) {
    // Same half cent as the amortization schedule this mirrors: $27,000 at 12.49% is exactly
    // $281.025, and `toFixed` rounds the double rather than that decimal, a cent light. The clamps
    // and sums around it are whole cents already.
    const interest = roundToCents((balance * rate) / 12)
    const scheduledPrincipal = Number(Math.min(Math.max(mortgagePayment - interest, 0), balance).toFixed(2))
    const balanceAfterScheduledPrincipal = balance - scheduledPrincipal
    const requestedAdditionalPrincipal =
      additionalPrincipalReductionPeriod > 0 && i % additionalPrincipalReductionPeriod === 0
        ? additionalPrincipalReductionAmount
        : 0
    const additionalPrincipal =
      addiPrincReductions[i - 1] ??
      Number(Math.min(Math.max(requestedAdditionalPrincipal, 0), balanceAfterScheduledPrincipal).toFixed(2))
    const endingBalance = Number(Math.max(balanceAfterScheduledPrincipal - additionalPrincipal, 0).toFixed(2))

    rows.push({ date: firstPaymentDate, balance: endingBalance })
    balance = endingBalance
    firstPaymentDate = DateTime.fromFormat(firstPaymentDate, 'MM/dd/yyyy').plus({ months: 1 }).toFormat('MM/dd/yyyy')
    if (i == data.ioMonths) mortgagePayment = removeComma(data.interestOnlyExpired)
  }

  return rows
}

const getUpbBeforeInvestorDate = (amortData: Record<string, any>, investorDate: DateTime) => {
  const priorDate = investorDate.minus({ months: 1 }).toFormat('MM/dd/yyyy')
  const rows = buildAmortizationBalances(amortData)
  const match = rows.find((row) => row.date === priorDate)
  if (match) return match.balance

  // Fall back to last payment strictly before the investor due date
  const priorRows = rows.filter((row) => {
    const rowDate = DateTime.fromFormat(row.date, 'MM/dd/yyyy')
    return rowDate.isValid && rowDate < investorDate
  })
  if (priorRows.length) return priorRows[priorRows.length - 1].balance

  // No prior payment yet — use original loan amount
  if (amortData?.loanAmount != null) return removeComma(amortData.loanAmount)
  return undefined
}

const getEffectiveInitialDeposit = (data: Record<string, any>, calculated: number) => {
  const ov = data[INITIAL_ESCROW_ACCOUNT_BALANCE_OV]
  if (ov !== undefined && ov !== null && ov !== '') return Num(removeComma(ov))
  return calculated
}

const buildAggregateEscrowBalances = (data: Record<string, any>) => {
  // Round money to cents at each step — JS floats turn 301.53+629.67 into 931.1999…,
  // and unrounded sums drift the projected escrow balance (Purchase Advice uses this).
  const round2 = (n: number) => Num(n.toFixed(2))
  const firstPaymentMonth = Number(formatDate(data['Loan.FirstPaymentDate'], 'M'))
  const firstPaymentYear = Number(formatDate(data['Loan.FirstPaymentDate'], 'yyyy'))
  const closingDateMonth = Number(formatDate(data['Status.SchedClosingDate'], 'M'))
  const closingDateYear = Number(formatDate(data['Status.SchedClosingDate'], 'yyyy'))
  if (!firstPaymentMonth || !firstPaymentYear) {
    return { initialDeposit: 0, rows: [] as Array<{ date: DateTime; month: number; monthName: string; balance: number }> }
  }

  let paymentToEscrow = 0
  const paymentFromEscrows: number[] = Array(12).fill(0)
  const descriptions: string[] = Array(12).fill('')

  let totalCush = 0
  for (let i = 0; i < 7; i++) {
    const disbursementSched = data[`PrepaidItems.${i}.DisbursementSched`]
    const payment = Number(data[`PrepaidItems.${i}.Payment`])
    const disbursementStartPur = data[`PrepaidItems.${i}.DisbursementStartPur`]
    const disbursementStartYear = data[`PrepaidItems.${i}.DisbursementStartYear`]
    const disbursementPeriods = data[`PrepaidItems.${i}.DisbursementPeriods`]
    const disbursementList = data[`PrepaidItems.${i}.DisbursementList`]?.split('|')
    const cushion = data[`PrepaidItems.${i}.CushionOV`] || data[`PrepaidItems.${i}._Cushion`] || 0
    const exactPeriodicAmount = data[`PrepaidItems.${i}.PeriodicPaymentAmount`]
    if (disbursementSched > 1) totalCush = round2(totalCush + round2(Num(cushion) * Num(payment)))

    if (disbursementSched > 1) paymentToEscrow = round2(paymentToEscrow + payment)
    if (disbursementSched == 2) {
      for (let j = 0; j < 12; j++) {
        paymentFromEscrows[j] = round2(paymentFromEscrows[j] + payment)
        if (descriptions[j]) descriptions[j] += ', '
        descriptions[j] += PrepaidItemDescMap[i]
      }
    } else if ([3, 4, 5].includes(Number(disbursementSched))) {
      if (disbursementStartPur == 0) continue
      let startMonth, startYear
      if (disbursementStartPur > 0 && disbursementStartPur < 13) {
        startMonth = disbursementStartPur
        startYear = disbursementStartYear
      } else {
        startMonth = closingDateMonth
        startYear = closingDateYear
      }
      let monthsInPeriod = 12
      let tolerance = 0.1
      if (disbursementSched == 3) {
        monthsInPeriod = 3
        tolerance = 0.025
      } else if (disbursementSched == 4) {
        monthsInPeriod = 6
        tolerance = 0.05
      }

      for (let j = (startMonth - firstPaymentMonth + 12) % 12; j < 12; j += monthsInPeriod) {
        if (!startYear || firstPaymentYear + (j + firstPaymentMonth > 12 ? 1 : 0) >= startYear) {
          let originPeriodicAmount = round2(Num(payment) * Num(monthsInPeriod))
          const exactPeriodicAmountNum = Num(exactPeriodicAmount)
          if (Math.round(Math.abs(originPeriodicAmount - exactPeriodicAmountNum) * 1000) / 1000 <= tolerance)
            originPeriodicAmount = exactPeriodicAmountNum
          paymentFromEscrows[j] = round2(paymentFromEscrows[j] + originPeriodicAmount)
          if (descriptions[j]) descriptions[j] += ', '
          descriptions[j] += PrepaidItemDescMap[i]
        }
      }
    } else if (disbursementSched == 6) {
      for (let j = 0; j < 12; j++) {
        if (disbursementPeriods[j] && disbursementPeriods[j] != '.') {
          const index = (j + 1 - firstPaymentMonth + 12) % 12
          paymentFromEscrows[index] = round2(
            paymentFromEscrows[index] +
              round2(Num(payment) * Num(disbursementPeriods[j].charCodeAt(0) - 'A'.charCodeAt(0) + 1)),
          )
          if (descriptions[index]) descriptions[index] += ', '
          descriptions[index] += PrepaidItemDescMap[i]
        }
      }
    } else if (disbursementSched == 7) {
      disbursementList?.map((v: any) => {
        const [date, amount] = v.split('~')
        const month = new Date(date).getMonth() + 1
        let year = new Date(date).getFullYear()
        if (month < firstPaymentMonth) year--
        if (year == firstPaymentYear) {
          const index = (month - firstPaymentMonth + 12) % 12
          paymentFromEscrows[index] = round2(paymentFromEscrows[index] + Num(amount))
          if (descriptions[index]) descriptions[index] += ', '
          descriptions[index] += PrepaidItemDescMap[i]
        }
      })
    }
  }

  const effectiveFromEscrows = applyPaymentFromEscrowOverrides(data, paymentFromEscrows)
  let minBal = 1000000000
  let trialBalance = 0
  for (let i = 0; i < 12; i++) {
    trialBalance = round2(trialBalance + paymentToEscrow - effectiveFromEscrows[i])
    if (minBal > trialBalance) minBal = trialBalance
  }

  const calcInitialDeposit = round2(totalCush - minBal)
  const initialDeposit = getEffectiveInitialDeposit(data, calcInitialDeposit)

  const rows: Array<{ date: DateTime; month: number; monthName: string; balance: number }> = []
  let running = initialDeposit
  for (let i = 0; i < 12; i++) {
    const absoluteMonthIndex = firstPaymentMonth - 1 + i
    const month = (absoluteMonthIndex % 12) + 1
    const year = firstPaymentYear + Math.floor(absoluteMonthIndex / 12)
    const date = DateTime.fromObject({ year, month, day: 1 }).startOf('day')
    running = round2(running + paymentToEscrow - effectiveFromEscrows[i])
    rows.push({ date, month, monthName: monthsInYear[month], balance: running })
  }
  return { initialDeposit, rows }
}

const getEscrowBalanceBeforeInvestorDate = (escrowData: Record<string, any>, investorDate: DateTime) => {
  const { initialDeposit, rows } = buildAggregateEscrowBalances(escrowData)
  if (!rows.length) return initialDeposit || undefined

  // Balance held just before the investor's first payment due date = ending balance of the
  // last Aggregate Escrow payment month strictly before that date. Matching by month number
  // alone was wrong (e.g. investor due 10/01 picked year-end September instead of Initial Deposit).
  const priorRows = rows.filter((row) => row.date.isValid && row.date < investorDate.startOf('day'))
  if (priorRows.length) return priorRows[priorRows.length - 1].balance
  return initialDeposit
}

const getConstructionBalance = (trustLedgerRes: Record<string, any>) => {
  const constructionReserve = trustLedgerRes?.constructionReserve
  if (constructionReserve === -1 || constructionReserve == null) return undefined

  let balance = removeComma(constructionReserve)
  const items = Array.isArray(trustLedgerRes?.data) ? trustLedgerRes.data : []
  items.forEach((item: Record<string, any>) => {
    const fee = solveDecimalJavascriptSum([
      removeComma(item.inspectionFee),
      removeComma(item.wireFee),
      removeComma(item.titleUpdateFee),
    ])
    balance = solveDecimalJavascriptSum([
      removeComma(balance),
      -1 * removeComma(item.payment),
      removeComma(item.deposit),
      -1 * removeComma(fee),
      -1 * removeComma(item.additionHoldback),
    ])
  })
  return Num(balance)
}

const getInterestReserveBalance = (interestRes: Record<string, any>) => {
  const interestReserve = interestRes?.interestReserve
  if (interestReserve === -1 || interestReserve == null) return undefined

  let balance = removeComma(interestReserve)
  const items = (interestRes?.interestData || []).filter(
    (item: Record<string, any>) => !item.type || item.type === 'interest',
  )
  items.forEach((item: Record<string, any>) => {
    balance = solveDecimalJavascriptSum([
      removeComma(balance),
      -1 * removeComma(item.payment),
      removeComma(item.deposit),
    ])
  })
  return Num(balance)
}

const getSecondaryNetPrice = (smData: Record<string, any>, side: FillProjectedSide) => {
  const isSellSide = side === 'buyer'
  const priceBase = isSellSide ? 'InvestorPriceBase' : 'BuyPriceBase'
  const srp = isSellSide ? 'InvestorSRP' : 'BuySRP'
  const pricingKey = isSellSide ? 'Sell' : 'Buy'
  const adjustments = smData?.Pricing?.[pricingKey] || []

  let netPrice = removeComma(smData?.[priceBase]) + removeComma(smData?.[srp])
  adjustments.forEach((item: Record<string, any>) => {
    netPrice += removeComma(item.PricePercent)
  })
  return Num(Number(netPrice).toFixed(3))
}

const applyNegativeConvention = (key: string, value: number) => {
  if ((NEGATIVE_PA_KEYS as readonly string[]).includes(key) && value > 0) return -1 * value
  return value
}

/**
 * Fetch and compute projected Purchase Advice wire-calc fields from
 * amort schedule, aggregate escrow, loan structure, ledgers, and secondary marketing.
 */
export async function fetchProjectedPurchaseAdviceValues(
  investorFirstPaymentDate: string,
  side: FillProjectedSide,
  loanMeta?: { isBPiece?: boolean; aPieceAmount?: any; proposedLoanAmount?: any },
): Promise<ProjectedPurchaseAdviceValues> {
  const investorDate = parseInvestorDate(investorFirstPaymentDate)
  if (!investorDate) return {}

  const [amortRes, escrowRes, trustRes, interestRes, smRes] = await Promise.all([
    getAmortizationSchedule().catch(() => null),
    gethud1PageData('escrow').catch(() => null),
    getTrustLedger().catch(() => null),
    getTrustLedgerInterest().catch(() => null),
    getSecondaryMarketingScreenData().catch(() => null),
  ])

  const result: ProjectedPurchaseAdviceValues = {}

  const upb = amortRes?.data ? getUpbBeforeInvestorDate(amortRes.data, investorDate) : undefined
  if (upb != null) result.PAUnpaidPrincipalBalance = upb

  const escrowBal = escrowRes?.data ? getEscrowBalanceBeforeInvestorDate(escrowRes.data, investorDate) : undefined
  if (escrowBal != null) result.PAEscrowBalance = applyNegativeConvention('PAEscrowBalance', escrowBal)

  if (loanMeta?.isBPiece) {
    const bPiece = removeComma(loanMeta.proposedLoanAmount) - removeComma(loanMeta.aPieceAmount)
    if (!Number.isNaN(bPiece)) result.BPiece = applyNegativeConvention('BPiece', bPiece)
  }

  const constructionBal = trustRes ? getConstructionBalance(trustRes) : undefined
  if (constructionBal != null)
    result.ConstructionReserve = applyNegativeConvention('ConstructionReserve', constructionBal)

  const interestBal = interestRes ? getInterestReserveBalance(interestRes) : undefined
  if (interestBal != null) result.InterestReserve = applyNegativeConvention('InterestReserve', interestBal)

  const smData = smRes?.data?.data || smRes?.data || smRes
  if (smData && typeof smData === 'object') {
    const netPrice = getSecondaryNetPrice(smData, side)
    if (netPrice != null && !Number.isNaN(netPrice)) result.PANetPrice = netPrice
  }

  return result
}

export const showPurchaseAdviceInterestCalculation = (productType: string) => {
  return ['Fix and Flip', 'Ground Up Construction', 'Bridge'].includes(productType)
}
